import React, { ReactNode } from 'react'
import styled from '@emotion/styled'
import { Search, DownloadIcon as LucideDownloadIcon } from 'lucide-react'
import Svg from '@components/svg'
import Button from '@components/button'
import theme from '@utils/theme'
import useIsMobile from '@hooks/useIsMobile'

/* ================================
   Styled Components
=================================== */

// Wrapper for the entire search section
const StyleTableSearchWrapper = styled.div`
  width: 98%;
  display: flex;
  justify-content: space-between;
  gap: 10px;
  position: relative;
  background-color: ${theme.colors.white};
  padding: 20px 10px;
  border-radius: 10px 10px 0 0;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    padding: 15px 0px;
  }
`

// Wrapper for search input + optional button
const SearchInputWrapper = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  gap: 10px;

  @media (max-width: 768px) {
    /* flex-direction: column; */
    width: 100%;
    gap: 8px;
  }
`

// Search input field styling
const StyleTableSearchInput = styled.input`
  width: 100%;
  height: 40px;
  border: 1px solid ${theme.colors.borderColor};
  border-radius: 8px;
  outline: none;
  padding: 0 10px;
  font-size: 14px;
  color: ${theme.colors.text};

  &::placeholder {
    color: ${theme.colors.placeholder};
  }
`

// Optional icon inside input
const StyleSearchIcon = styled(Search)`
  color: ${theme.colors.placeholder};
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  width: 1.1rem;

  @media (max-width: 768px) {
    left: 10px;
  }
`

// Text on left side of search input
const Text = styled.div`
  flex: 1;
  color: ${theme.colors.primary};
  font-size: 18px;
  line-height: 100%;
  letter-spacing: 0.25px;
  font-weight: 300;
  display: flex;
  align-items: center;
  gap: 10px;
`

/* ================================
   SearchInput Component
=================================== */
const SearchInput = ({
  searchInput,
  setSearchInput,
  placeholder,
  hasButton = false,
  buttonLabel = 'Download',
  onButtonClick,
  icon,
  styleSearchLeftText,
  text,
  styleText,
  hasChildren,
  children,
}: {
  searchInput: string
  setSearchInput: (val: string) => void
  placeholder: string[]
  hasButton?: boolean
  buttonLabel?: string
  onButtonClick?: () => void
  icon?: string
  hasText?: boolean
  text?: string
  styleText?: React.CSSProperties
  hasChildren?: boolean
  children?: ReactNode
  styleSearchLeftText?: string
}) => {
  const isMobile = useIsMobile()
  return (
    <StyleTableSearchWrapper>
      {!!text && (
        <Text style={{ ...styleText }}>
          <Svg src={icon} styleSvg="transform:translateY(1px)" />
          {text}
        </Text>
      )}
      {!!hasChildren && <div style={{ flex: 1 }}>{children}</div>}
      <SearchInputWrapper>
        <StyleTableSearchInput
          type="text"
          placeholder={'Search Here'}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        {!!hasButton && (
          <Button
            icon={<LucideDownloadIcon width={18} />}
            title={!isMobile && 'Download'}
            onClick={onButtonClick}
            styleButton={`
              background: transparent;
              border: 1px solid ${theme.colors.primary};
              color: ${theme.colors.primary};
              padding: ${isMobile ? '4px' : '8px'};
            `}
          />
        )}
      </SearchInputWrapper>
    </StyleTableSearchWrapper>
  )
}

export default SearchInput
